import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { EIR_DEADLINE_PHRASE } from "@shared/constants/jurisdiction-ontario";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ArrowRight, Download, FileSpreadsheet, Info, Loader2, Pencil, RotateCcw, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { FIELD_KEYS } from '@shared/constants/fields';
import { formatApproxCAD as fmt, formatApproxNetEstateDisplay } from "@/lib/estate-format";

// ── Types ──────────────────────────────────────────────────────────────────────

interface AssetItem {
  id: string;
  entryId: string | null;
  category: string;
  sectionSlug: string;
  description: string;
  plannerValue: number;
  overrideValue: number | null;
  effectiveValue: number;
  ownership: "sole" | "joint";
  passesProbate: boolean;
  probateReason: string | null;
}

interface LiabilityItem {
  id: string;
  entryId: string | null;
  category: string;
  sectionSlug: string;
  description: string;
  value: number;
}

interface FlowItem {
  id: string;
  source: string;
  amount: number;
  rawFrequency: string;
  monthlyAmount: number;
}

interface EstateSummary {
  assets: { items: AssetItem[]; totalValue: number; probateValue: number; nonProbateValue: number };
  liabilities: { items: LiabilityItem[]; totalValue: number };
  netEstateValue: number;
  monthlyIncome: { items: FlowItem[]; total: number };
  monthlyExpenses: { items: FlowItem[]; total: number };
  monthlyNet: number;
  eat: { probateValue: number; taxAmount: number; isSmallEstate: boolean; formType: string; note: string };
  completeness: { sectionsWithValues: number; totalAssetSections: number; missingValueSections: string[] };
}

// ── Formatting ─────────────────────────────────────────────────────────────────


// ── Override input for executor ───────────────────────────────────────────────

function OverrideInput({
  clientId,
  item,
  onSaved,
  overridesList,
}: {
  clientId: string;
  item: AssetItem;
  onSaved: () => void;
  overridesList: Array<{ id: string; entryId: string }>;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(item.overrideValue !== null ? String(item.overrideValue) : "");

  const saveMutation = useMutation({
    mutationFn: async (value: string) => {
      if (!item.entryId) throw new Error("No entry to override");
      const res = await apiRequest("POST", `/api/clients/${clientId}/estate-overrides`, {
        entryId: item.entryId,
        sectionSlug: item.sectionSlug,
        fieldKey: FIELD_KEYS.VALUE,
        overrideValue: value,
      });
      return res.json();
    },
    onSuccess: () => {
      setEditing(false);
      onSaved();
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "estate-overrides"] });
      toast({ title: "Override saved" });
    },
    onError: () => toast({ title: "Failed to save override", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const override = overridesList.find(o => o.entryId === item.entryId);
      if (!override) return;
      await apiRequest("DELETE", `/api/clients/${clientId}/estate-overrides/${override.id}`);
    },
    onSuccess: () => {
      onSaved();
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "estate-overrides"] });
      toast({ title: "Override removed" });
    },
    onError: () => toast({ title: "Failed to remove override", variant: "destructive" }),
  });

  if (!editing) {
    return (
      <div className="text-right">
        <div className="text-sm font-medium">{fmt(item.effectiveValue)}</div>
        {item.overrideValue !== null && (
          <div className="text-xs text-muted-foreground line-through">{fmt(item.plannerValue)}</div>
        )}
        <div className="flex justify-end gap-1 mt-0.5">
          {item.overrideValue !== null && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 text-primary border-primary/40">Executor value</Badge>
          )}
          {item.entryId && (
            <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-primary">
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="text-xs text-muted-foreground">Planner's value: {fmt(item.plannerValue)}</div>
      <div className="flex gap-1">
        <Input
          className="h-7 w-28 text-xs text-right"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder="0"
          autoFocus
        />
        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => saveMutation.mutate(inputValue)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
      {item.overrideValue !== null && (
        <button
          className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
          onClick={() => deleteMutation.mutate()}
        >
          <RotateCcw className="w-3 h-3" /> Revert to planner's value
        </button>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface Props {
  clientId: string;
  isExecutor?: boolean;
  plannerName?: string;
  dateOfDeath?: string | null;
}

export default function EstateSummaryPage({ clientId, isExecutor = false, plannerName, dateOfDeath }: Props) {
  const { data: summary, isLoading, refetch } = useQuery<EstateSummary>({
    queryKey: ["/api/clients", clientId, "estate-summary"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/estate-summary`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: overridesList = [] } = useQuery<Array<{ id: string; entryId: string; fieldKey: string }>>({
    queryKey: ["/api/clients", clientId, "estate-overrides"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/estate-overrides`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isExecutor,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!summary) return null;

  const hasData = summary.completeness.sectionsWithValues >= 1;
  const isPartial = summary.completeness.sectionsWithValues < summary.completeness.totalAssetSections / 2;

  const assetsByCategory = new Map<string, AssetItem[]>();
  for (const item of summary.assets.items) {
    if (!assetsByCategory.has(item.category)) assetsByCategory.set(item.category, []);
    assetsByCategory.get(item.category)!.push(item);
  }

  const liabsByCategory = new Map<string, LiabilityItem[]>();
  for (const item of summary.liabilities.items) {
    if (!liabsByCategory.has(item.category)) liabsByCategory.set(item.category, []);
    liabsByCategory.get(item.category)!.push(item);
  }

  return (
    <TooltipProvider>
      <div className="container max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-primary" />
              Estate Financial Summary
            </h1>
            {isExecutor && plannerName && (
              <p className="text-sm text-muted-foreground mt-1">
                Estate of {plannerName}{dateOfDeath ? ` · Passed away ${dateOfDeath}` : ""}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/api/clients/${clientId}/estate-summary/export/pdf`, "_blank")}
            >
              <Download className="w-4 h-4 mr-1.5" />
              PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/api/clients/${clientId}/estate-summary/export/excel`, "_blank")}
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              Excel
            </Button>
          </div>
        </div>

        {/* Partial data warning */}
        {isPartial && hasData && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This summary is based on partial data ({summary.completeness.sectionsWithValues} of {summary.completeness.totalAssetSections} asset categories). Fill in more categories for a complete picture.
          </div>
        )}

        {/* Card 1: Balance Sheet — deep-link anchors for executor task drawer (Build 4b-2) */}
        <Card id="net-worth" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="text-base">Balance Sheet — Net Estate Value</CardTitle>
          </CardHeader>
          <CardContent>
            {!hasData ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No financial data entered yet. Fill in bank accounts, investments, and other assets to see your balance sheet.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {/* Assets column */}
                <div id="asset-summary" className="scroll-mt-24">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Assets</h3>
                  {Array.from(assetsByCategory.entries()).map(([cat, items]) => (
                    <div key={cat} className="mb-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">{cat}</p>
                      {items.map(item => (
                        <div key={item.id} className="flex justify-between items-start py-1 text-sm border-b border-border/40 last:border-0">
                          <div className="flex-1 mr-2">
                            <span>{item.description}</span>
                            {!item.passesProbate && item.probateReason && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0">Non-probate</Badge>
                                </TooltipTrigger>
                                <TooltipContent>{item.probateReason}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          {isExecutor ? (
                            <OverrideInput clientId={clientId} item={item} onSaved={() => refetch()} overridesList={overridesList} />
                          ) : (
                            <span className="font-medium tabular-nums">{fmt(item.effectiveValue)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                  {summary.completeness.missingValueSections.map(label => (
                    <div key={label} className="flex justify-between items-center py-1 text-sm text-muted-foreground border-b border-border/40 last:border-0">
                      <span>{label} — no value entered</span>
                      <span className="text-xs text-primary inline-flex items-center gap-1">Add<ArrowRight className="h-3 w-3" aria-hidden="true" /></span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold text-sm pt-3 border-t">
                    <span>Total Assets</span>
                    <span>{fmt(summary.assets.totalValue)}</span>
                  </div>
                </div>

                {/* Liabilities column */}
                <div id="debts" className="scroll-mt-24">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Liabilities</h3>
                  {summary.liabilities.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No liabilities entered</p>
                  ) : (
                    Array.from(liabsByCategory.entries()).map(([cat, items]) => (
                      <div key={cat} className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">{cat}</p>
                        {items.map(item => (
                          <div key={item.id} className="flex justify-between py-1 text-sm border-b border-border/40 last:border-0">
                            <span>{item.description}</span>
                            <span className="font-medium tabular-nums">({fmt(item.value)})</span>
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                  <div className="flex justify-between font-semibold text-sm pt-3 border-t">
                    <span>Total Liabilities</span>
                    <span>({fmt(summary.liabilities.totalValue)})</span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer totals */}
            {hasData && (
              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Net Estate Value</span>
                  {/* A negative net estate (insolvent) renders in parenthesized
                      accounting style + destructive color — never bare abs(). */}
                  <span className={`font-bold text-xl ${summary.netEstateValue < 0 ? "text-destructive" : "text-primary"}`}>
                    {formatApproxNetEstateDisplay(summary.netEstateValue)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Probate Estate Value</span>
                    <Tooltip>
                      <TooltipTrigger><Info className="w-3.5 h-3.5 text-muted-foreground" /></TooltipTrigger>
                      <TooltipContent className="max-w-xs">Assets that pass through the Ontario probate process. Joint assets and those with named beneficiaries are excluded.</TooltipContent>
                    </Tooltip>
                  </div>
                  <span className="font-medium">{fmt(summary.assets.probateValue)}</span>
                </div>
                {summary.eat.isSmallEstate && (
                  <p className="text-xs text-primary">Your estate may qualify for the simplified Small Estate Certificate (Form 74.1A)</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card 2: Monthly Cash Flow */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Cash Flow</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Income</h3>
                {summary.monthlyIncome.items.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No income entered</p>
                ) : (
                  summary.monthlyIncome.items.map(item => (
                    <div key={item.id} className="flex justify-between py-1 text-sm border-b border-border/40 last:border-0">
                      <div>
                        <span>{item.source}</span>
                        <span className="text-xs text-muted-foreground ml-1.5">({item.rawFrequency})</span>
                      </div>
                      <span className="tabular-nums">{fmt(item.monthlyAmount)}/mo</span>
                    </div>
                  ))
                )}
                <div className="flex justify-between font-semibold text-sm pt-3 border-t">
                  <span>Total Income</span>
                  <span>{fmt(summary.monthlyIncome.total)}/mo</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Expenses</h3>
                {summary.monthlyExpenses.items.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No expenses entered</p>
                ) : (
                  summary.monthlyExpenses.items.map(item => (
                    <div key={item.id} className="flex justify-between py-1 text-sm border-b border-border/40 last:border-0">
                      <div>
                        <span>{item.source}</span>
                        <span className="text-xs text-muted-foreground ml-1.5">({item.rawFrequency})</span>
                      </div>
                      <span className="tabular-nums">{fmt(item.monthlyAmount)}/mo</span>
                    </div>
                  ))
                )}
                <div className="flex justify-between font-semibold text-sm pt-3 border-t">
                  <span>Total Expenses</span>
                  <span>({fmt(summary.monthlyExpenses.total)})/mo</span>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <span className="font-bold">Monthly Net</span>
              <span className={`font-bold text-lg ${summary.monthlyNet >= 0 ? "text-green-600" : "text-destructive"}`}>
                {summary.monthlyNet >= 0 ? "+" : "-"}{fmt(summary.monthlyNet)}/mo
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: EAT — deep-link anchor (Build 4b-2) */}
        <Card id="eat" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="text-base">Estate Administration Tax (Ontario)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Province</span>
                <span>Ontario <span className="text-xs text-muted-foreground">(more coming soon)</span></span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Probate Value</span>
                <span className="font-medium">{fmt(summary.assets.probateValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax Rate</span>
                <span>$15 per $1,000 over $50,000</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Est. EAT</span>
                <span className="font-bold text-primary">{fmt(summary.eat.taxAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Form Type</span>
                <span className="font-medium">{summary.eat.formType}</span>
              </div>
            </div>
            {isExecutor && (
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-primary">
                You will need the probate estate value for your application. The Estate Information Return must be filed {EIR_DEADLINE_PHRASE}.
              </div>
            )}
            <p className="text-xs text-muted-foreground">{summary.eat.note} Consult your estate lawyer.</p>
            {summary.eat.isSmallEstate && (
              <p className="text-xs text-primary font-medium">Your estate qualifies for the Small Estate Certificate (Form 74.1A) — a simplified process.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
